"use client";

import { useEffect } from "react";
import Link from "next/link";
import { useI18n } from "@/i18n/context";
import { LEGAL_NAV, LEGAL_HEADER } from "./legalParts";
import "./legal.css";

type Active = "faq" | "cgu" | "privacy";

export function LegalShell({ active, panel }: { active: Active; panel: string }) {
  const { locale, toggleLocale } = useI18n();

  // Accordion (FAQ), TOC highlight (terms/privacy), language button.
  useEffect(() => {
    const root = document.querySelector<HTMLElement>(".legal-scope");
    if (!root) return;

    // FAQ accordion
    const faqHandlers: Array<[HTMLElement, () => void]> = [];
    root.querySelectorAll<HTMLElement>(".faq-item").forEach((item) => {
      const q = item.querySelector<HTMLElement>(".faq-q");
      const a = item.querySelector<HTMLElement>(".faq-a");
      if (!q || !a) return;
      const h = () => {
        if (item.classList.contains("open")) {
          item.classList.remove("open");
          a.style.maxHeight = "";
        } else {
          item.classList.add("open");
          a.style.maxHeight = a.scrollHeight + "px";
        }
      };
      q.addEventListener("click", h);
      faqHandlers.push([q, h]);
    });
    const recompute = () => {
      root.querySelectorAll<HTMLElement>(".faq-item.open .faq-a").forEach((a) => {
        a.style.maxHeight = a.scrollHeight + "px";
      });
    };
    window.addEventListener("resize", recompute);

    // Language button → shared i18n context
    const langBtn = root.querySelector<HTMLElement>("#lang");
    const onLang = () => toggleLocale();
    langBtn?.addEventListener("click", onLang);

    // TOC active highlight
    let io: IntersectionObserver | undefined;
    const tocLinks = root.querySelectorAll<HTMLElement>(".legal-toc a");
    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              const id = (e.target as HTMLElement).id;
              tocLinks.forEach((l) =>
                l.classList.toggle("active", l.getAttribute("href") === "#" + id),
              );
            }
          });
        },
        { rootMargin: "-140px 0px -70% 0px" },
      );
      root.querySelectorAll<HTMLElement>(".legal-body section").forEach((s) => io!.observe(s));
    }

    return () => {
      faqHandlers.forEach(([el, h]) => el.removeEventListener("click", h));
      window.removeEventListener("resize", recompute);
      langBtn?.removeEventListener("click", onLang);
      io?.disconnect();
    };
  }, [toggleLocale, active]);

  // Reflect active language on the toggle + remeasure open answers.
  useEffect(() => {
    const root = document.querySelector<HTMLElement>(".legal-scope");
    if (!root) return;
    document.documentElement.lang = locale;
    root.querySelectorAll<HTMLElement>("#lang [data-lang]").forEach((s) => {
      s.classList.toggle("active", s.dataset.lang === locale);
    });
    const id = window.setTimeout(() => {
      root.querySelectorAll<HTMLElement>(".faq-item.open .faq-a").forEach((a) => {
        a.style.maxHeight = a.scrollHeight + "px";
      });
    }, 60);
    return () => window.clearTimeout(id);
  }, [locale]);

  const tabClass = (key: Active) => (active === key ? "tab-btn active" : "tab-btn");

  return (
    <div className="legal-scope">
      <div dangerouslySetInnerHTML={{ __html: LEGAL_NAV }} />
      <div dangerouslySetInnerHTML={{ __html: LEGAL_HEADER }} />

      <div className="tabs">
        <div className="tabs-inner" role="tablist">
          <Link className={tabClass("faq")} href="/faq">
            FAQ
          </Link>
          <Link className={tabClass("cgu")} href="/legal/cgu">
            <span className="en">Terms</span>
            <span className="fr">CGU</span>
          </Link>
          <Link className={tabClass("privacy")} href="/legal/privacy">
            <span className="en">Privacy</span>
            <span className="fr">Confidentialité</span>
          </Link>
        </div>
      </div>

      <main>
        <section className="panel active" role="tabpanel" dangerouslySetInnerHTML={{ __html: panel }} />
      </main>

      <footer>
        <div className="footer-inner">
          <div>
            <span className="en">© WooZoo 2026 · A clear gaze</span>
            <span className="fr">© WooZoo 2026 · Un regard clair</span>
          </div>
          <div className="links">
            <Link href="/">
              <span className="en">Home</span>
              <span className="fr">Accueil</span>
            </Link>
            <Link href="/legal/cgu">
              <span className="en">Terms</span>
              <span className="fr">CGU</span>
            </Link>
            <Link href="/legal/privacy">
              <span className="en">Privacy</span>
              <span className="fr">Confidentialité</span>
            </Link>
            <a href="/legal/privacy#p-10" data-consent-open>
              <span className="en">Cookies</span>
              <span className="fr">Cookies</span>
            </a>
            <Link href="/legal/suppression">
              <span className="en">My data</span>
              <span className="fr">Mes données</span>
            </Link>
            <a href="https://instagram.com/woozoo">Instagram</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
